import { toast } from 'sonner';
import { parse } from 'tldts';

import { normalizeDomainList } from '@/lib/normalize-domain';

// A value the search can't use (a LinkedIn "URL" that isn't one, a domain
// with no dot) used to become a chip anyway; the request builder then dropped
// it, so the search ignored it or, as the only filter, 400'd with "At least
// one filter is required". Same rules as the request (normalizeDomainList,
// normalizePersonLinkedInUrl), checked when the value is added.

/** A bare, searchable hostname, or null. */
export const validDomain = (value: string): string | null => {
  const domain = normalizeDomainList([value])[0];
  return domain && /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain) ? domain : null;
};

/** One pasted Company line, sorted by its shape. A domain is URL-shaped
 *  ("https://acme.com", "www.acme.com", "acme.com/about") or lowercase with
 *  a known TLD ("acme.com"), stored normalized. "J.Crew" and "E.ON" stay
 *  names. A lowercase dotted line with no space that isn't a valid domain
 *  ("acme.com,") is invalid, so it is reported, not searched as a name. */
export function classifyPastedCompany(
  raw: string
):
  | { kind: 'domain'; value: string }
  | { kind: 'name'; value: string }
  | { kind: 'invalid'; value: string } {
  const value = raw.trim();
  const urlShaped =
    /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ||
    /^www\./i.test(value) ||
    (!/\s/.test(value) && value.includes('.') && value.includes('/'));
  const dotted = !/\s/.test(value) && value.includes('.');
  if (!urlShaped && !(dotted && value === value.toLowerCase()))
    return { kind: 'name', value };
  const domain = validDomain(value);
  if (domain && (urlShaped || parse(domain).isIcann))
    return { kind: 'domain', value: domain };
  return { kind: 'invalid', value };
}

export const rejectValues = (values: string[], noun: string) => {
  const sample = values[0].trim().slice(0, 60);
  toast.error(
    values.length === 1
      ? `"${sample}" isn't a valid ${noun}`
      : `Skipped ${values.length} entries that aren't a valid ${noun}`
  );
};

/** Normalized values, deduped; anything invalid is dropped and reported. */
export const keepValid = (
  values: readonly string[],
  normalize: (v: string) => string | null,
  noun: string
): string[] => {
  const out = new Set<string>();
  const rejected: string[] = [];
  for (const raw of values) {
    const value = normalize(raw);
    if (value) out.add(value);
    else if (raw.trim()) rejected.push(raw);
  }
  if (rejected.length) rejectValues(rejected, noun);
  return Array.from(out);
};

/** Each inverted numeric min/max pair swapped: "500 to 100" can only match
 *  nobody and still costs a search, and the range meant is the same numbers.
 *  ``bases`` name the pairs (``connections`` → connectionsMin/connectionsMax). */
export function withOrderedRanges<T extends object>(
  filters: T,
  bases: readonly string[]
): T {
  const read = filters as Record<string, unknown>;
  const num = (value: unknown) =>
    typeof value === 'string' && value.trim() ? Number(value) : NaN;
  let out = filters;
  for (const base of bases) {
    const min = read[`${base}Min`];
    const max = read[`${base}Max`];
    if (num(min) > num(max))
      out = { ...out, [`${base}Min`]: max, [`${base}Max`]: min };
  }
  return out;
}
